import { useCallback, useEffect, useState } from 'react'
import Modal from 'react-modal'
import { AdminStickerDetailDto, ModerationContentType } from '@ivy-monorepo/shared'
import { BaseModalProps } from 'src/types/modal'
import FormItem from '../FormItem'
import ModerationActionsBar from '../moderation/ModerationActionsBar'
import { adminService } from 'src/services/admin.service'

interface StickerModalProps extends BaseModalProps {
  publicId: string | null
  onDeleted?: (publicId: string) => void
  onModerationChange?: () => void
  onDelete: (publicId: string) => Promise<void>
}

const StickerModal = ({ isOpen, onClose, publicId, onDelete, onDeleted, onModerationChange }: StickerModalProps) => {
  const [detail, setDetail] = useState<AdminStickerDetailDto | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isPinning, setIsPinning] = useState(false)

  const fetchDetail = useCallback(async () => {
    if (!publicId) {
      setDetail(null)
      return
    }

    try {
      setIsLoading(true)
      const response = await adminService.getStickerDetail(publicId)
      setDetail(response)
    } catch (err) {
      console.log('Failed to fetch sticker detail', err)
      setDetail(null)
    } finally {
      setIsLoading(false)
    }
  }, [publicId])

  useEffect(() => {
    if (!isOpen) {
      setDetail(null)
      return
    }

    fetchDetail()
  }, [fetchDetail, isOpen])

  const handleModerationChange = () => {
    onModerationChange?.()
  }

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      ariaHideApp={false}
      className={`modal-popup confirm-popup sticker-detail-popup${detail?.isFlagged ? ' moderation-flagged-content' : ''}`}
      overlayClassName="modal-overlay"
      shouldCloseOnEsc={true}
      shouldCloseOnOverlayClick={true}
      onAfterClose={() => {
        document.body.classList.remove('ReactModal__Body--open')
      }}
    >
      <div className="modal-content-wrapper">
        <div className="moderation-modal-header">
          <h2>Sticker</h2>
          <div className="moderation-modal-header-flags">
            {detail?.isFlagged && <span className="moderation-flag-badge">Sticker flagged</span>}
            {detail?.isExpired && <span className="moderation-flag-badge">Expired</span>}
            {detail?.isPinned && <span className="moderation-flag-badge pinned">Pinned by the Venue</span>}
          </div>
        </div>

        {isLoading && <div className="breadcrumb-detail-loading">Loading sticker details...</div>}

        {!isLoading && detail && (
          <div className="photo-verification-detail-layout">
            <div className="photo-verification-detail-photo">
              <img src={detail.imageUrl} alt="Sticker" />
            </div>

            <div className="photo-verification-detail-stats">
              <FormItem label="Author" value={`${detail.author.firstName} ${detail.author.lastName}`} />
              <FormItem label="Venue" value={detail.venue.name} />
              {detail.caption && <FormItem label="Caption" value={detail.caption} />}
              <FormItem label="Posted" value={detail.createdAt} type="datetime" />
              {detail.expiresAt ? (
                <FormItem label="Expires" value={detail.expiresAt} type="datetime" />
              ) : (
                <FormItem label="Expires" value="Never (kept on profile)" />
              )}
              <FormItem label="Likes" value={String(detail.likeCount ?? 0)} />
              {(detail.openReportCount ?? 0) > 0 && (
                <FormItem label="Open reports" value={String(detail.openReportCount)} />
              )}

              <button
                className="styled-button"
                disabled={isPinning}
                onClick={async () => {
                  try {
                    setIsPinning(true)
                    await adminService.setStickersPinned([detail.publicId], !detail.isPinned)
                    setDetail(current => (current ? { ...current, isPinned: !current.isPinned } : current))
                    handleModerationChange()
                  } finally {
                    setIsPinning(false)
                  }
                }}
              >
                {detail.isPinned ? 'Unpin' : 'Pin to venue'}
              </button>
              {detail.latitude != null && detail.longitude != null && (
                <FormItem label="Location" value={`${detail.latitude}, ${detail.longitude}`} />
              )}

              <ModerationActionsBar
                authorPublicId={detail.author.publicId}
                contentType={ModerationContentType.Sticker}
                contentReferenceId={detail.publicId}
                quotedContent={detail.caption || '[sticker photo]'}
                isFlagged={detail.isFlagged}
                onFlagChange={flagged => {
                  setDetail(current => (current ? { ...current, isFlagged: flagged } : current))
                  handleModerationChange()
                }}
                onDelete={async () => {
                  await onDelete(detail.publicId)
                  onDeleted?.(detail.publicId)
                }}
                deleteConfirmTitle="Delete sticker?"
                deleteConfirmBody="This will hide the sticker from users. Content is retained for audit."
              />
            </div>
          </div>
        )}

        <div className="modal-footer-content">
          <button className="styled-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}

export default StickerModal
